import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Reports, useActiveShow } from "common-fe";
import { useTranslation } from "i18n-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useShowRides } from "../hooks/useShowRides";
import { formatRideWho } from "../components/RideLink";
import {
  classNumberLabel,
  sortByClassNumber,
  classDayKey,
  formatDayKey,
} from "../util/classDisplay";
import { reportName, reportParamLabel } from "../util/reportLabels";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

// Searchable label for a ride in the competitor lookup (#560): horse / rider
// names + CNO + ride number + class, so typing any of them filters to it.
// Falls back to CNO / ride number when a manual ride has no resolved names.
// #860 — the "who" half reuses RideLink's shared formatRideWho, so the
// rider/horse text reads the same as everywhere else; the CNO/ride#/class
// suffix stays lookup-specific (a link makes no sense inside an
// Autocomplete option).
function rideLookupLabel(r, onWord = "on") {
  const who = formatRideWho(r, onWord);
  const cno = r.competitorNumber != null ? `CNO ${r.competitorNumber}` : null;
  const num = r.rideNumber != null ? `#${r.rideNumber}` : null;
  const cls = [num, r.className].filter(Boolean).join(" ");
  return [who || cno || cls || r.id, who && cno ? cno : null, cls || null]
    .filter(Boolean)
    .join(" · ");
}

// The score-admin Reports page (#490, #551). Mounts the shared common-fe report
// components filtered to the score domain, and supplies score-specific param
// help so an admin never types raw ids (#551):
//   - showId comes from the active show (context) — hidden, not typed.
//   - day + ringId render as dropdowns populated from the show's classes.
//   - classId renders as a filter/lookup combobox (used by the final-class report).
// The app already runs inside ScoreadminActiveShow/OrgProvider (App.jsx), so the
// report-api client sends X-Org-Id and useActiveShow/useScoreApi are available.
const REPORT_API_BASE_URL = import.meta.env.VITE_REPORT_API_BASE_URL;

const distinctSorted = (values) =>
  [...new Set(values.filter((v) => v !== undefined && v !== null && v !== ""))].sort((a, b) =>
    String(a).localeCompare(String(b), undefined, { numeric: true }),
  );

export default function ReportsPage() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.reports"));
  const scoreApi = useScoreApi();
  // #2195 — activeShowId reads null both before the show list resolves and
  // once it resolves to genuinely nothing; only `loading` tells them apart,
  // so it has to gate the "no show" text too, not just activeShowId.
  const { activeShowId, activeShow, loading: loadingShow } = useActiveShow();
  // #579 — the ride page deep-links here with the report + showId/rideId
  // prefilled; those params fill + hide their form fields (one-click run).
  const [searchParams] = useSearchParams();
  const qsReport = searchParams.get("report") || undefined;
  const qsRideId = searchParams.get("rideId") || undefined;
  const qsShowId = searchParams.get("showId") || undefined;
  // #928 — RideEditor's incomplete-judges override dialog deep-links here with
  // this set, so the (hidden, auto-filled) report param reaches competitor-
  // score-sheet's fetchData and lets it proceed + stamp instead of blocking.
  const qsOverrideIncompleteJudges = searchParams.get("overrideIncompleteJudges") === "1";
  // #1995 — a rideId in the query means this landed here from a ride's own
  // "Score Sheet Report" / "Print Anyway" deep-link with every required param
  // already known (showId + rideId). That's the one case where the extra
  // "Run report" click before the PDF generates is pure friction — run it
  // immediately instead. This flag only says the CONDITION for that one
  // deep-link was met; it is Reports.jsx's own autoRanRef + initialSelectedId
  // scoping (judge fix) that actually confines the auto-run to that single
  // def, once, so a later manual list pick always still needs its own click.
  const autoRun = Boolean(qsReport && qsRideId);
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  // #560/#1906 — the show's rides power the competitor score-sheet lookup
  // (rideId). useShowRides is always progressive: the most recent day's
  // rides resolve first (dropping `loadingRides`), the rest merge in behind
  // them while `loadingMore` stays true — options grow in place, so the
  // lookup Autocomplete keeps its spinner up (via `ridesLoading` below)
  // until the whole show's rides have landed, without blocking search on
  // whatever has already arrived.
  const { rides, loading: loadingRides, loadingMore: loadingMoreRides } = useShowRides(activeShowId);
  const ridesLoading = loadingRides || loadingMoreRides;

  // Load the active show's classes to derive day/ring options + the class lookup.
  useEffect(() => {
    if (!activeShowId) {
      setClasses([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingClasses(true);
        const list = unwrap(await scoreApi.classes.listClasses(activeShowId));
        if (!cancelled) setClasses(list);
      } catch {
        if (!cancelled) setClasses([]);
      } finally {
        if (!cancelled) setLoadingClasses(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeShowId, scoreApi]);

  // #634 — the Day dropdown's VALUE is the day key the report defs filter on (a
  // naive YYYY-MM-DD slice of the class's scheduledTime); the label is the
  // friendly "Fri, Jul 17". It used to offer a short weekday ("Fri") derived in
  // PST, which no report could ever match — hence the empty day reports.
  //
  // #1909 — prefer the show's per-show summary row (dayKeys already ascending,
  // rings already in ringsFor's natural-numeric-plus-tiebreak order) over
  // reducing the class list. This picker is a one-shot page-load filter list,
  // not a live poll-driven display, so summary's reconcile-cadence freshness
  // is fine here (unlike announcer-fe's OrderOfGo tab row, deliberately left
  // on its own live derivation, see that file's #1740 history). Falls back to
  // the class-list reduction when summary is absent (not yet re-reconciled).
  const days = useMemo(() => {
    const keys = activeShow?.summary?.dayKeys?.length
      ? activeShow.summary.dayKeys
      : distinctSorted(classes.map((c) => classDayKey(c)));
    return keys.map((key) => ({ value: key, label: formatDayKey(key) }));
  }, [activeShow?.summary, classes]);
  const rings = useMemo(
    () =>
      activeShow?.summary?.rings?.length
        ? activeShow.summary.rings
        : distinctSorted(classes.map((c) => c.ring)),
    [activeShow?.summary, classes],
  );
  const sortedClasses = useMemo(() => sortByClassNumber(classes), [classes]);

  // #649 — report names / param labels originate in packages/report-defs (raw
  // English `name` / `paramsSchema.*.title`) and never passed through i18n. The
  // static code→key maps live in ../util/reportLabels.js; common-fe stays
  // i18n-free and just calls these resolvers with whatever it has (a def, or a
  // bare id/key) — see packages/common-fe/src/components/reports/Reports.jsx.
  const resolveReportName = (def) => reportName(t, def?.id, def?.name);
  const resolveParamLabel = (def, key, schema) =>
    reportParamLabel(t, key, schema?.title || key);

  const paramsContext = useMemo(
    () => ({
      showId: qsShowId || activeShowId,
      // Only inject rideId when deep-linked; otherwise the ride lookup renders.
      ...(qsRideId ? { rideId: qsRideId } : {}),
      // #928 — always supplied (default false) so any report def declaring
      // this param (currently only competitor-score-sheet) auto-fills it as a
      // hidden field rather than showing a bare, easy-to-miss checkbox.
      overrideIncompleteJudges: qsOverrideIncompleteJudges,
    }),
    [qsShowId, activeShowId, qsRideId, qsOverrideIncompleteJudges],
  );

  // Field renderers keyed by param name; applied whenever a report def declares
  // that param. A field with no options still renders (empty dropdown) so the
  // report can't be run against a stale free-typed id.
  const paramsFieldRenderers = useMemo(() => {
    // #601 — a small spinner in the select's adornment while its options load,
    // so an empty dropdown reads as "loading" rather than "nothing here".
    const selectRenderer =
      (options, loading) =>
      // eslint-disable-next-line react/display-name -- a field-render callback, not a mounted component
      ({ value, onChange, label, required, error, disabled }) => (
        <TextField
          select
          fullWidth
          label={label}
          required={required}
          error={error}
          disabled={disabled}
          helperText={error ? t("reports.required") : undefined}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          InputProps={{
            endAdornment: loading ? (
              <CircularProgress size={18} sx={{ mr: 3 }} />
            ) : undefined,
          }}
        >
          {/* #634 — an option is either a plain string (ring) or a
              { value, label } pair (day: an ISO day key shown as "Fri, Jul 17"). */}
          {options.map((opt) => {
            const value = typeof opt === "object" ? opt.value : opt;
            const optLabel = typeof opt === "object" ? opt.label : opt;
            return (
              <MenuItem key={value} value={value}>
                {optLabel}
              </MenuItem>
            );
          })}
        </TextField>
      );

    const classRenderer = ({ value, onChange, label, required, error, disabled }) => (
      <Autocomplete
        fullWidth
        loading={loadingClasses}
        disabled={disabled}
        options={sortedClasses}
        value={sortedClasses.find((c) => c.id === value) ?? null}
        onChange={(_e, sel) => onChange(sel?.id ?? "")}
        getOptionLabel={(c) => classNumberLabel(c)}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={error}
            helperText={error ? t("reports.required") : undefined}
          />
        )}
      />
    );

    // #560 — competitor score-sheet lookup: no one knows the ride id, so find a
    // ride by horse / rider name or CNO. Label uses the given field label
    // ("Ride") but the placeholder hints at the search.
    const rideRenderer = ({ value, onChange, label, required, error, disabled }) => (
      <Autocomplete
        fullWidth
        loading={ridesLoading}
        disabled={disabled}
        options={rides}
        value={rides.find((r) => r.id === value) ?? null}
        onChange={(_e, sel) => onChange(sel?.id ?? "")}
        getOptionLabel={(r) => rideLookupLabel(r, t("rideEditor.onHorse"))}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={error}
            placeholder={t("reports.rideLookupPlaceholder")}
            helperText={error ? t("reports.required") : t("reports.rideLookupHint")}
          />
        )}
      />
    );

    return {
      day: selectRenderer(days, loadingClasses),
      ringId: selectRenderer(rings, loadingClasses),
      classId: classRenderer,
      rideId: rideRenderer,
    };
  }, [days, rings, sortedClasses, rides, loadingClasses, ridesLoading, t]);

  return (
    <Box sx={{ p: 2 }}>
      {!activeShowId ? (
        loadingShow ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress data-testid="reports-page-show-loading" />
          </Box>
        ) : (
          <Typography color="text.secondary">{t("reports.noShow")}</Typography>
        )
      ) : (
        <Reports
          basePath={REPORT_API_BASE_URL}
          domain="score"
          title={t("nav.reports")}
          // #602 — the shared Reports chrome is i18n-free; feed it translated
          // strings so nothing renders hardcoded English.
          labels={{
            availableReports: t("reports.availableReports"),
            runHistory: t("reports.runHistory"),
            reportDialogTitle: t("reports.reportDialogTitle"),
            reportStarted: t("reports.reportStarted"),
            failedToStart: t("reports.failedToStart"),
            runningTitle: t("reports.runningTitle"),
            runningMessage: t("reports.runningMessage"),
            runningDoneTitle: t("reports.runningDoneTitle"),
            runningDoneMessage: t("reports.runningDoneMessage"),
            runningFailedTitle: t("reports.runningFailedTitle"),
            download: t("reports.download"),
            close: t("reports.close"),
            clearHistory: t("reports.clearHistory"),
            clearConfirm: t("reports.clearConfirm"),
            delete: t("reports.delete"),
            noReports: t("reports.noReports"),
            failedToLoadReports: t("reports.failedToLoadReports"),
            runReport: t("reports.runReport"),
            required: t("reports.required"),
            noRuns: t("reports.noRuns"),
            failedToLoadRuns: t("reports.failedToLoadRuns"),
            colReport: t("reports.colReport"),
            colRequested: t("reports.colRequested"),
            colStatus: t("reports.colStatus"),
            colActions: t("reports.colActions"),
            view: t("reports.view"),
            runFailed: t("reports.runFailed"),
            runNotReady: t("reports.runNotReady"),
            failedToLoadReport: t("reports.failedToLoadReport"),
            openPdf: t("reports.openPdf"),
            openHtml: t("reports.openHtml"),
            reportPreview: t("reports.reportPreview"),
          }}
          initialSelectedId={qsReport}
          autoRun={autoRun}
          paramsContext={paramsContext}
          paramsFieldRenderers={paramsFieldRenderers}
          resolveReportName={resolveReportName}
          resolveParamLabel={resolveParamLabel}
        />
      )}
    </Box>
  );
}
